/**
 * index.ts — Desktop Bootstrap Entry Point (Renderer Side)
 *
 * Аналог mobile-app/src/mobileInit.ts для Desktop.
 *
 * HOW TO INTEGRATE в webvideo-client-dev/src/index.tsx:
 *
 *   import { bootstrapDesktop } from '../../desktop-app/src/desktop';
 *   // В цепочке промисов перед createApp():
 *   .then(() => bootstrapDesktop())
 *   .then(() => createApp())
 *
 * Безопасен для вызова в браузере — все вызовы защищены проверкой isDesktop.
 */

import { isDesktop, isMac } from './platform';
import { onDeepLink, updater, setDockBadge } from './ipc';

export { isDesktop, isMac, isWindows, platform, desktopVersion } from './platform';
export { initDesktopNotifications, notifyNewMessage, notifyIncomingCall } from './notifications';
export {
  keychain,
  getScreenCaptureSources,
  windowControls,
  setDockBadge,
  incomingCall,
  sessionCookie,
} from './ipc';

/**
 * Инициализация desktop-специфичных функций.
 * Вызывать один раз при запуске React-приложения.
 */
export async function bootstrapDesktop(options?: {
  onDeepLinkNavigate?: (path: string) => void;
  onNotificationAction?: (chatId: string) => void;
  onMenuNavigate?: (path: string) => void;
  onUpdateAvailable?: (version: string) => void;
  onUpdateDownloaded?: (version: string) => void;
}): Promise<void> {
  if (!isDesktop) return;

  console.log('[Desktop] Bootstrapping Electron desktop integration...');

  /** Добавляем CSS-класс для desktop-специфичных стилей */
  document.body.classList.add('is-desktop');
  if (isMac) document.body.classList.add('is-mac');

  /** Обработка deep links (smeet://) */
  onDeepLink((url) => {
    console.log('[Desktop] Deep link received:', url);
    options?.onDeepLinkNavigate?.(url);
  });

  /** Навигация из нативного меню (⌘1, ⌘2, Настройки…) */
  window.smeet?.on('navigate', (path) => {
    options?.onMenuNavigate?.(path as string);
  });

  /** Клик по нативному macOS уведомлению → переход в чат */
  window.smeet?.on('notification:action', (chatId) => {
    console.log('[Desktop] Notification tapped, chatId:', chatId);
    options?.onNotificationAction?.(chatId as string);
  });

  /** Подписка на события обновлений */
  updater.onAvailable((info) => {
    console.log('[Desktop] Update available:', info.version);
    options?.onUpdateAvailable?.(info.version);
  });

  updater.onDownloaded((info) => {
    console.log('[Desktop] Update downloaded:', info.version);
    options?.onUpdateDownloaded?.(info.version);
  });

  console.log('[Desktop] Bootstrap complete');
}

/**
 * Обновить badge с непрочитанными сообщениями.
 * Вызывать при изменении счётчика непрочитанных.
 */
export function updateUnreadBadge(totalUnread: number): void {
  setDockBadge(totalUnread);
}

/**
 * Инициализация push-уведомлений после авторизации пользователя.
 */
export async function setupDesktopPushAfterLogin(
  navigateToChat: (chatId: string) => void
): Promise<void> {
  if (!isDesktop) return;

  const { initDesktopNotifications } = await import('./notifications');
  await initDesktopNotifications({
    onNotificationTapped: (chatId) => {
      console.log('[Desktop Push] Notification tapped, navigating to chat:', chatId);
      navigateToChat(chatId);
    },
  });
}
